/**
 * Invoices Page - Updated with full-page preview
 * Manage water billing invoices
 */

import React, { useState, useEffect, useRef } from 'react';
import { InvoiceList } from '@components/billing/invoice_list';
import { InvoiceForm } from '@components/billing/invoice_form';
import { InvoicePreview } from '@components/billing/invoice_preview';
import { PageLoading } from '@components/common/loading_spinner';
import { toast } from 'react-toastify';
import {
  Invoice,
  CreateInvoicePayload,
  InvoiceCreateData,
  InvoiceTemplate as InvoiceTemplateType,
  InvoiceEmailPayload
} from '@/types/invoice_types';
import { Client } from '@/types/client_types';
import {
  fetch_invoices,
  create_invoice,
  update_invoice,
  send_invoice_email,
  download_invoice_pdf,
  fetch_invoice_template_config
} from '@/services/invoice_service';
import { fetch_clients } from '@/services/client_service';

type PageView = 'list' | 'preview';

export const InvoicesPage: React.FC = () => {
  // State management
  const [invoices, set_invoices] = useState<Invoice[]>([]);
  const [clients, set_clients] = useState<Client[]>([]);
  const [template_config, set_template_config] = useState<InvoiceTemplateType | null>(null);
  const [is_loading, set_is_loading] = useState(true);
  const [load_error, set_load_error] = useState<string | null>(null);
  const [is_form_open, set_is_form_open] = useState(false);
  const [is_submitting, set_is_submitting] = useState(false);
  const [current_view, set_current_view] = useState<PageView>('list');

  const [selected_invoice, set_selected_invoice] = useState<Invoice | null>(null);
  const [editing_invoice, set_editing_invoice] = useState<Invoice | null>(null);
  const [form_mode, set_form_mode] = useState<'create' | 'edit'>('create');

  // Use ref to track timeout
  const timeout_ref = useRef<number | null>(null);

  // Load data on mount
  useEffect(() => {
    load_initial_data();

    // Cleanup timeout on unmount
    return () => {
      if (timeout_ref.current !== null) {
        window.clearTimeout(timeout_ref.current);
      }
    };
  }, []);

  const load_initial_data = async () => {
    try {
      set_is_loading(true);
      set_load_error(null);

      // Set timeout for loading
      timeout_ref.current = window.setTimeout(() => {
        set_is_loading(false);
        set_load_error('Loading timed out. Please try again.');
        toast.error('Loading timed out. Please refresh the page.');
      }, 30000) as unknown as number; // 30 second timeout

      // Load invoices, clients, and template config in parallel
      const [invoices_data, clients_data, config_data] = await Promise.all([
        fetch_invoices().catch(err => {
          console.error('Failed to fetch invoices:', err);
          toast.error('Failed to load invoices');
          return [];
        }),
        fetch_clients().catch(err => {
          console.error('Failed to fetch clients:', err);
          toast.error('Failed to load clients');
          return [];
        }),
        fetch_invoice_template_config().catch(err => {
          console.error('Failed to fetch template config:', err);
          toast.error('Failed to load invoice template');
          return null;
        })
      ]);

      // Clear timeout since loading completed
      if (timeout_ref.current !== null) {
        window.clearTimeout(timeout_ref.current);
        timeout_ref.current = null;
      }

      console.log("invoices_data", invoices_data);
      set_invoices(invoices_data);
      set_clients(clients_data);
      set_template_config(config_data);

      // Check if critical data failed to load
      if (!config_data) {
        set_load_error('Failed to load invoice template configuration');
      } else if (invoices_data.length === 0 && clients_data.length === 0) {
        // This is a legitimate empty state, not an error
        console.log('No invoices or clients found');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      set_load_error('Failed to load invoices data');
      toast.error('Failed to load invoices data');
      
      // Clear timeout on error
      if (timeout_ref.current !== null) {
        window.clearTimeout(timeout_ref.current);
        timeout_ref.current = null;
      }
    } finally {
      set_is_loading(false);
    }
  };

  const handle_add_invoice = () => {
    set_form_mode('create');
    set_editing_invoice(null);
    set_is_form_open(true);
  };

  const handle_edit_invoice = (invoice: Invoice) => {
    set_form_mode('edit');
    set_editing_invoice(invoice);
    set_is_form_open(true);
    set_current_view('list'); // Return to list view when editing
  };

  const handle_view_invoice = (invoice: Invoice) => {
    set_selected_invoice(invoice);
    set_current_view('preview');
  };

  const handle_back_to_list = () => {
    set_current_view('list');
    set_selected_invoice(null);
  };

  /**
   * Transform CreateInvoicePayload to InvoiceCreateData
   * Maps the form data structure to the API expected structure
   */
  const transform_invoice_payload = (payload: CreateInvoicePayload): InvoiceCreateData => {
    return {
      client: parseInt(payload.client),
      invoice_date: payload.issue_date,
      due_date: payload.due_date,
      billing_period_start: payload.billing_period_start,
      billing_period_end: payload.billing_period_end,
      items: payload.line_items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total: item.amount
      })),
      meter_reading: payload.meter_reading,
      tax_rate: payload.tax_rate,
      discount_amount: payload.discount_amount,
      notes: payload.notes,
      terms: payload.terms,
      payment_instructions: payload.payment_instructions,
      status: payload.status
    };
  };

  const handle_form_submit = async (data: CreateInvoicePayload) => {
    try {
      set_is_submitting(true);

      // Transform the payload to match API expectations
      const api_payload = transform_invoice_payload(data);

      if (form_mode === 'create') {
        const new_invoice = await create_invoice(api_payload);
        toast.success('Invoice created successfully');

        // If status is not draft, send email
        if (data.status !== 'draft' && new_invoice.client_email) {
          await handle_send_invoice_internal(new_invoice);
        }
      } else if (editing_invoice) {
        await update_invoice(editing_invoice.id, api_payload);
        toast.success('Invoice updated successfully');
      }

      set_is_form_open(false);
      await load_initial_data();
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error(`Failed to ${form_mode === 'create' ? 'create' : 'update'} invoice`);
    } finally {
      set_is_submitting(false);
    }
  };

  const handle_send_invoice_internal = async (invoice: Invoice) => {
    if (!invoice.client_email) {
      toast.error('Client email not found. Cannot send invoice.');
      return;
    }

    try {
      const email_payload: InvoiceEmailPayload = {
        invoice_id: invoice.id,
        recipient_email: invoice.client_email,
        subject: `Water Bill Invoice - ${invoice.invoice_number}`,
        message: `Dear ${invoice.client_name},\n\nPlease find attached your water bill invoice for the period ${new Date(invoice.billing_period_start).toLocaleDateString()} to ${new Date(invoice.billing_period_end).toLocaleDateString()}.\n\nTotal Amount: KES ${invoice.total_amount.toLocaleString()}\nDue Date: ${new Date(invoice.due_date).toLocaleDateString()}\n\nThank you for your business.`
      };

      await send_invoice_email(email_payload);
      toast.success('Invoice sent successfully');
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send invoice email');
    }
  };

  const handle_send_invoice = async (invoice: Invoice) => {
    if (!window.confirm(`Send invoice ${invoice.invoice_number} to ${invoice.client_name}?`)) {
      return;
    }

    await handle_send_invoice_internal(invoice);
    await load_initial_data();
  };

  const handle_download_invoice = async (invoice: Invoice) => {
    try {
      toast.info('Generating PDF...');
      
      const pdf_blob = await download_invoice_pdf(invoice.id);
      
      // Verify it's actually a PDF
      if (!pdf_blob.type.includes('pdf') && pdf_blob.type !== 'application/octet-stream') {
        console.error('Invalid blob type:', pdf_blob.type);
        throw new Error(`Invalid file type: ${pdf_blob.type}`);
      }
      
      // Verify blob has content
      if (pdf_blob.size === 0) {
        throw new Error('Received empty PDF file');
      }
      
      console.log('PDF blob received:', {
        size: pdf_blob.size,
        type: pdf_blob.type
      });

      // Create download link
      const url = window.URL.createObjectURL(pdf_blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${invoice.invoice_number}.pdf`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success('Invoice downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading invoice:', error);
      
      let error_message = 'Failed to download invoice PDF';
      
      if (error?.message) {
        error_message = error.message;
      } else if (typeof error === 'string') {
        error_message = error;
      }
      
      if (error_message.includes('no line items') || error_message.includes('no items')) {
        toast.error('Cannot generate PDF: Invoice has no line items. Please add items first.');
      } else if (error_message.includes('no client')) {
        toast.error('Cannot generate PDF: Invoice has no client assigned.');
      } else if (error_message.includes('Invalid file type')) {
        toast.error('Server returned invalid file format. Please check the invoice data.');
      } else {
        toast.error(error_message);
      }
      
      console.error('Full error details:', {
        error,
        invoice_id: invoice.id,
        invoice_number: invoice.invoice_number,
        has_items: invoice.items?.length > 0,
        has_client: !!invoice.client_name
      });
    }
  };

  const handle_print = () => {
    window.print();
  };

  const handle_export = async () => {
    try {
      toast.info('Exporting invoices...');

      const csv_data = invoices.map(inv => ({
        'Invoice #': inv.invoice_number,
        'Client': inv.client_name,
        'Issue Date': new Date(inv.issue_date).toLocaleDateString(),
        'Due Date': new Date(inv.due_date).toLocaleDateString(),
        'Amount': inv.total_amount,
        'Paid': inv.amount_paid,
        'Balance': (inv as any).amount_outstanding,
        'Status': inv.status
      }));

      const headers = Object.keys(csv_data[0]);
      const csv_content = [
        headers.join(','),
        ...csv_data.map(row => headers.map(header => `"${row[header as keyof typeof row]}"`).join(','))
      ].join('\n');

      const blob = new Blob([csv_content], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoices_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Invoices exported successfully');
    } catch (error) {
      console.error('Error exporting invoices:', error);
      toast.error('Failed to export invoices');
    }
  };

  // Loading state
  if (is_loading) {
    return <PageLoading message="Loading invoices..." />;
  }

  // Show preview view
  if (current_view === 'preview' && template_config) {
    return (
      <InvoicePreview
        invoice={selected_invoice}
        template_config={template_config}
        on_download={handle_download_invoice}
        on_send={handle_send_invoice}
        on_edit={handle_edit_invoice}
        on_print={handle_print}
        on_back={handle_back_to_list}
      />
    );
  }

  // Error state with retry option
  if (load_error) {
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto mt-20 text-center">
          <div className="bg-error bg-opacity-10 border border-error rounded-lg p-6">
            <svg
              className="mx-auto h-12 w-12 text-error"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-semibold text-error">
              Failed to Load
            </h3>
            <p className="mt-2 text-sm text-error">{load_error}</p>
            <button
              onClick={load_initial_data}
              className="mt-6 px-4 py-2 bg-error text-white rounded-md hover:bg-opacity-90 transition-colors"
            >
              Retry Loading
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show list view
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-charcoal">Invoices</h1>
          <p className="text-medium-gray mt-1">
            Create, manage, and track water billing invoices
          </p>
        </div>
      </div>

      {/* Invoice List */}
      <InvoiceList
        invoices={invoices}
        is_loading={is_loading}
        on_add_invoice={handle_add_invoice}
        on_view_invoice={handle_view_invoice}
        on_edit_invoice={handle_edit_invoice}
        on_send_invoice={handle_send_invoice}
        on_download_invoice={handle_download_invoice}
        on_export={handle_export}
      />

      {/* Invoice Form Modal */}
      <InvoiceForm
        is_open={is_form_open}
        on_close={() => set_is_form_open(false)}
        on_submit={handle_form_submit}
        initial_data={editing_invoice || undefined}
        clients={clients}
        is_loading={is_submitting}
        mode={form_mode}
      />
    </div>
  );
};